import { FC, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Search } from 'lucide-react';

import type { DayPlanGroupRow } from '@trafficpulse/shared';
import { SegmentedPills } from '@/components/ui';
import { ListGroupScopeProvider, useListGroupResolve } from '@/features/list-groups';

import { GroupChip } from '../shared/GroupChip';
import { GroupListRows } from './lists/GroupListRows';
import { defaultScope, emptyListsText, type ListScope, scopeLists, searchScopes, todaySetOf } from './lists/list-scope';

const FILTER_AFTER = 8;

/** The group's lists as the row's container campaign reads them (19/10/2026) — the planner's own view. */
export const GroupListsPopover: FC<{ group: DayPlanGroupRow; campaignWiserId?: number | null }> = ({ group, campaignWiserId }) => (
  <ListGroupScopeProvider campaignWiserId={campaignWiserId}>
    <GroupLists group={group} />
  </ListGroupScopeProvider>
);

/** All · Today (19/10/2026; "Not today" removed 23/10/2026, client QA) — opens on Today when the row has today's lists; the search reads
 *  inside the chosen filter and the pills count its matches in each. Every open starts fresh (default
 *  filter, empty search) — never an old search inside a filter it was not typed in. */
const GroupLists: FC<{ group: DayPlanGroupRow }> = ({ group }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<ListScope | null>(null);
  const resolved = useListGroupResolve(group.listGroupId, open);
  const today = todaySetOf(group);
  const scopes = scopeLists(resolved.data?.lists ?? [], today);
  const scope = picked ?? defaultScope(today);
  const matches = searchScopes(scopes, query);
  const onOpenChange = (next: boolean) => {
    if (next) {
      setPicked(null);
      setQuery('');
    }
    setOpen(next);
  };

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={`Show lists in ${group.groupName}`}
          title={`Show all lists in ${group.groupName}`}
          className="inline-flex min-w-0 max-w-full rounded-full outline-none transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 [&>span]:min-w-0"
        >
          <GroupChip name={group.groupName} color={group.groupColor} />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="end"
          sideOffset={6}
          collisionPadding={12}
          className="z-[80] flex w-[min(22rem,calc(100vw-1.5rem))] flex-col gap-2 rounded-xl border border-border bg-popover p-3 text-popover-foreground shadow-xl animate-in fade-in-0 zoom-in-95"
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <Header group={group} resolved={resolved} />
          {scopes.all.length > FILTER_AFTER ? (
            <ListSearch groupName={group.groupName} query={query} onQuery={setQuery} size={scopes[scope].length} />
          ) : null}
          {resolved.data && scopes.all.length > 0 ? (
            <ScopePills scope={scope} counts={matches} onChange={setPicked} />
          ) : null}
          <div className="max-h-72 overflow-y-auto overscroll-contain rounded-lg border border-border/60 bg-background [scrollbar-gutter:stable]">
            <GroupListRows state={resolved} rows={matches[scope]} empty={emptyListsText(scope, group.groupName, query)} />
          </div>
          {resolved.data?.count !== undefined && resolved.data.count !== resolved.data.totalLists ? (
            <p className="text-[11px] leading-snug text-muted-foreground">{resolved.data.count} of {resolved.data.totalLists} country lists currently match this group&apos;s rule.</p>
          ) : null}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

const ScopePills: FC<{ scope: ListScope; counts: Record<ListScope, unknown[]>; onChange: (scope: ListScope) => void }> = ({ scope, counts, onChange }) => (
  <div className="flex flex-col gap-1">
    <SegmentedPills
      options={[
        { value: 'all', label: 'All', count: counts.all.length },
        { value: 'today', label: 'Today', count: counts.today.length },
      ]}
      value={scope}
      onChange={onChange}
      className="[&>div]:flex [&>div]:w-full [&_button]:flex-1 [&_button]:justify-center"
    />
    {scope === 'today' && counts.today.length > 1 ? <p className="text-[11px] text-muted-foreground">In today&apos;s send order — first sent on top.</p> : null}
  </div>
);

const Header: FC<{ group: DayPlanGroupRow; resolved: ReturnType<typeof useListGroupResolve> }> = ({ group, resolved }) => (
  <div className="flex items-start justify-between gap-3">
    <div className="min-w-0">
      <p className="truncate text-sm font-semibold">{group.groupName}</p>
      <p className="text-xs text-muted-foreground">
        {resolved.isLoading
          ? 'Loading matching lists…'
          : resolved.data
            ? `${resolved.data.count} matching ${resolved.data.count === 1 ? 'list' : 'lists'}`
            : 'Lists in this group'}
      </p>
    </div>
    <GroupChip name={group.groupName} color={group.groupColor} />
  </div>
);

const ListSearch: FC<{ groupName: string; query: string; onQuery: (query: string) => void; size: number }> = ({ groupName, query, onQuery, size }) => (
  <label className="flex h-9 items-center gap-2 rounded-lg border border-input bg-background px-2.5 focus-within:border-primary/60">
    <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
    <input
      autoFocus
      value={query}
      onChange={(event) => onQuery(event.target.value)}
      placeholder={`Filter ${size} ${size === 1 ? 'list' : 'lists'}…`}
      aria-label={`Filter lists in ${groupName}`}
      className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
    />
  </label>
);
